import { useMemo } from 'react';
import { Group, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { ShieldCheck, Eye, AlertTriangle, HelpCircle } from 'lucide-react';
import { Badge, Card } from '../ui';
import type { CoverageCell, CoverageStatus, CoverageSummary } from '../../api/types';
import { colors } from '../../theme/tokens';

/**
 * AttackCoverageMatrix — reusable ATT&CK-style coverage grid for rank-7 BAS
 * (breach-and-attack-simulation). Renders one tile per technique, grouped by
 * tactic into ATT&CK-navigator-style columns, colored by coverage verdict:
 *   prevented → neon green   (a control blocked it before it ran)
 *   detected  → hud-cyan     (it ran and the defenses alerted)
 *   missed    → hud-red      (it ran and nothing fired — the coverage gap)
 *   errored   → muted grey   (the harness could not fire it — not a verdict)
 *
 * It is intentionally presentational and data-source-agnostic (takes a flat
 * cell list + an optional summary) so a future ATT&CK navigator view can reuse
 * the same grid. Numeric fields arrive as strings off the coverage store wire,
 * so we coerce defensively.
 */

export interface AttackCoverageMatrixProps {
  cells: ReadonlyArray<CoverageCell>;
  summary?: CoverageSummary;
  /** Optional header shown above the grid (e.g. range name / run id). */
  caption?: string;
  /** Slot rendered to the right of the header (e.g. a "Run BAS sweep" button). */
  action?: React.ReactNode;
}

const STATUS_META: Record<
  CoverageStatus,
  { label: string; color: string; Icon: typeof ShieldCheck }
> = {
  prevented: { label: 'Prevented', color: colors.neon, Icon: ShieldCheck },
  detected: { label: 'Detected', color: colors.hudCyan, Icon: Eye },
  missed: { label: 'Missed', color: colors.hudRed, Icon: AlertTriangle },
  errored: { label: 'Errored', color: '#71717a', Icon: HelpCircle },
};

const STATUS_ORDER: ReadonlyArray<CoverageStatus> = ['prevented', 'detected', 'missed', 'errored'];

function toNum(v: string | number | undefined): number {
  if (typeof v === 'number') return v;
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function tileBg(color: string): string {
  // 14% tint of the verdict color over the HUD panel.
  return `${color}22`;
}

/** Group cells by tactic, preserving first-seen tactic order. */
function groupByTactic(
  cells: ReadonlyArray<CoverageCell>,
): ReadonlyArray<{ tactic: string; cells: ReadonlyArray<CoverageCell> }> {
  const order: string[] = [];
  const buckets = new Map<string, CoverageCell[]>();
  for (const c of cells) {
    const tactic = (c.tactic && c.tactic.trim()) || 'uncategorized';
    if (!buckets.has(tactic)) {
      buckets.set(tactic, []);
      order.push(tactic);
    }
    buckets.get(tactic)!.push(c);
  }
  return order.map((tactic) => ({ tactic, cells: buckets.get(tactic)! }));
}

function CoverageTile({ cell }: { cell: CoverageCell }) {
  const status = (STATUS_META[cell.status] ? cell.status : 'errored') as CoverageStatus;
  const meta = STATUS_META[status];
  const detections = toNum(cell.detection_count);
  const rules = (cell.detecting_rule_ids ?? '').split(',').filter(Boolean);
  const label =
    `${meta.label} — ${cell.technique_id}` +
    (cell.name ? ` ${cell.name}` : '') +
    (status === 'detected' && detections ? ` · ${detections} detection${detections === 1 ? '' : 's'}` : '') +
    (rules.length ? ` · rules ${rules.join(', ')}` : '') +
    (cell.detail ? ` · ${cell.detail}` : '');
  return (
    <Tooltip label={label} withArrow multiline w={260} color="dark">
      <Stack
        gap={2}
        p={6}
        style={{
          borderRadius: 6,
          border: `1px solid ${meta.color}`,
          background: tileBg(meta.color),
          minWidth: 0,
          cursor: 'default',
        }}
      >
        <Text size="xs" ff="JetBrains Mono" fw={600} c="rgb(var(--c-txt-bright))" truncate>
          {cell.technique_id}
        </Text>
        <Text size="10px" c="dimmed" lineClamp={2} style={{ minHeight: 24 }}>
          {cell.name || '—'}
        </Text>
        <Group gap={4} wrap="nowrap">
          <meta.Icon size={11} color={meta.color} />
          {status === 'detected' && detections ? (
            <Text size="10px" ff="JetBrains Mono" c={meta.color}>
              {detections}
            </Text>
          ) : null}
        </Group>
      </Stack>
    </Tooltip>
  );
}

export function AttackCoverageMatrix({ cells, summary, caption, action }: AttackCoverageMatrixProps) {
  const columns = useMemo(() => groupByTactic(cells), [cells]);

  // Prefer the server summary; fall back to counting the cells we were given so
  // the headline is always coherent with what's on screen.
  const counts = useMemo(() => {
    if (summary && summary.total !== undefined) {
      return {
        total: toNum(summary.total),
        prevented: toNum(summary.prevented),
        detected: toNum(summary.detected),
        missed: toNum(summary.missed),
        errored: toNum(summary.errored),
        score: toNum(summary.coverage_score),
      };
    }
    const c = { total: cells.length, prevented: 0, detected: 0, missed: 0, errored: 0 };
    for (const cell of cells) {
      if (cell.status in c) c[cell.status] = (c[cell.status] ?? 0) + 1;
    }
    const scored = c.total - c.errored;
    return { ...c, score: scored ? (c.prevented + c.detected) / scored : 0 };
  }, [summary, cells]);

  const pct = Math.round(counts.score * 100);

  if (cells.length === 0) {
    return (
      <Card>
        <Stack gap="xs">
          {action ? (
            <Group justify="space-between">
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                ATT&CK coverage
              </Text>
              {action}
            </Group>
          ) : null}
          <Text size="sm" c="dimmed">
            No coverage scored yet. Run a BAS sweep to fire the technique library at this range
            and score what the defenses prevented, detected, and missed.
          </Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      <Card>
        <Stack gap="sm">
          <Group justify="space-between" align="start">
            <Stack gap={2}>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                ATT&CK coverage
              </Text>
              {caption ? (
                <Text size="xs" c="dimmed" ff="JetBrains Mono">
                  {caption}
                </Text>
              ) : null}
            </Stack>
            {action}
          </Group>

          <Group gap="lg" align="end">
            <Stack gap={0}>
              <Text size="32px" fw={700} c={colors.neon} ff="JetBrains Mono" lh={1}>
                {pct}%
              </Text>
              <Text size="10px" tt="uppercase" c="dimmed" fw={600}>
                coverage · {counts.total} techniques
              </Text>
            </Stack>
            <Group gap="sm" wrap="wrap">
              {STATUS_ORDER.map((s) => {
                const meta = STATUS_META[s];
                const n = (counts as Record<string, number>)[s] ?? 0;
                return (
                  <Group key={s} gap={6} wrap="nowrap">
                    <meta.Icon size={13} color={meta.color} />
                    <Text size="sm" ff="JetBrains Mono" c="rgb(var(--c-txt-bright))">
                      {n}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {meta.label}
                    </Text>
                  </Group>
                );
              })}
            </Group>
          </Group>
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing="sm">
        {columns.map((col) => (
          <Stack key={col.tactic} gap={6}>
            <Group gap={6} wrap="nowrap">
              <Text size="10px" tt="uppercase" fw={700} c="dimmed" ff="JetBrains Mono" truncate>
                {col.tactic}
              </Text>
              <Badge variant="neutral" size="xs">
                {col.cells.length}
              </Badge>
            </Group>
            <Stack gap={6}>
              {col.cells.map((cell) => (
                <CoverageTile key={`${cell.tactic}:${cell.technique_id}`} cell={cell} />
              ))}
            </Stack>
          </Stack>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
